import { Box, Typography } from "@mui/material";
import { INTAKE_COLORS, INTAKE_FONT } from "../../theme/intakeTokens";
import { INTAKE_PROFILE_ROW_PX } from "../layout/layoutConstants";
import IntakeHeaderBackButton from "./IntakeHeaderBackButton";
import AiProvenanceLink from "../AiProvenanceLink";

/**
 * Back-arrow placement, measured to the arrow's ink rather than to its hit
 * target: 24px from the header's left edge, 16px to the title's first letter.
 * IntakeHeaderBackButton is a 32x32 button holding a 20x16 arrow at left: 6px,
 * and Lato sets its capitals ~1px inside the text box, so both figures need
 * those corrections backed out before they become CSS.
 */
const BACK_ARROW_INK_LEFT_PX = 24;
const BACK_ARROW_INK_GAP_RIGHT_PX = 16;
const BACK_ARROW_HIT_TARGET_INSET_PX = 6;
const BACK_ARROW_BUTTON_SIZE_PX = 32;
const TITLE_LEFT_SIDE_BEARING_PX = 1;
const HEADER_INSET_LEFT_PX = BACK_ARROW_INK_LEFT_PX - BACK_ARROW_HIT_TARGET_INSET_PX;
const BACK_ARROW_GAP_RIGHT_PX =
  BACK_ARROW_INK_GAP_RIGHT_PX - BACK_ARROW_HIT_TARGET_INSET_PX - TITLE_LEFT_SIDE_BEARING_PX;
/**
 * The row keeps an 8px gap for the badge and timestamp; the extra pixel the
 * arrow needs rides on the title alone so those other gaps stay untouched.
 */
const HEADER_ROW_GAP_PX = 8;
const TITLE_NUDGE_LEFT_PX = BACK_ARROW_GAP_RIGHT_PX - HEADER_ROW_GAP_PX;
/** Indents the second row so the description starts under the title, not the arrow. */
const HEADER_TEXT_COLUMN_INSET_PX = BACK_ARROW_BUTTON_SIZE_PX + BACK_ARROW_GAP_RIGHT_PX;

export default function IntakeProfileHeader({
  appName,
  startedAt,
  overallPct = 0,
  factsFilled = 0,
  factsTotal = 0,
  projectId,
  onBack,
}) {
  const progressPct = factsTotal ? Math.round((factsFilled / factsTotal) * 100) : overallPct;
  const startedDate = startedAt ? new Date(startedAt) : null;
  const startedShort =
    startedDate && !Number.isNaN(startedDate.getTime())
      ? startedDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : "";

  return (
    <Box
      sx={{
        pl: `${HEADER_INSET_LEFT_PX}px`,
        pr: INTAKE_PROFILE_ROW_PX,
        py: 1,
        boxSizing: "border-box",
        bgcolor: INTAKE_COLORS.surface,
        borderBottom: `1px solid ${INTAKE_COLORS.grey20}`,
        fontFamily: INTAKE_FONT,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: `${HEADER_ROW_GAP_PX}px`,
              flexWrap: "wrap",
              mb: 0.5,
              width: "100%",
            }}
          >
            <IntakeHeaderBackButton onClick={onBack} />
            <Typography
              sx={{
                ml: `${TITLE_NUDGE_LEFT_PX}px`,
                fontSize: 18,
                fontWeight: 700,
                color: INTAKE_COLORS.text,
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
                flexShrink: 1,
              }}
            >
              {appName || "New onboarding application"}
            </Typography>
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: "20px",
                bgcolor: INTAKE_COLORS.amberBadge,
                color: INTAKE_COLORS.amberBadgeText,
                fontSize: 10,
                fontWeight: 700,
                lineHeight: 1.3,
                flexShrink: 0,
              }}
            >
              In progress • {overallPct}% confidence
            </Box>
            {/* Facts come from uploads, pasted text and chat answers (plus the archetype): link to all Gate 0 AI runs. */}
            <AiProvenanceLink
              projectId={projectId}
              steps={["Start intake chat", "Document upload", "Pasted text", "Chat answer", "Archetype"]}
              target="gate"
              refreshKey={factsFilled}
            />
          </Box>

          <Typography
            sx={{
              fontSize: 12,
              color: INTAKE_COLORS.textSecondary,
              lineHeight: 1.45,
              minWidth: 0,
              pl: `${HEADER_TEXT_COLUMN_INSET_PX}px`,
              whiteSpace: "normal",
              wordBreak: "normal",
              overflowWrap: "break-word",
            }}
          >
            Describe your AI or share your docs. We'll capture what your assurance review needs in about 8 minutes.
          </Typography>
        </Box>

        <Box
          sx={{
            width: 288,
            maxWidth: "100%",
            height: 82,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "8px",
            px: "12px",
            py: "8px",
            borderRadius: "12px",
            border: "1px solid #E6E6E6",
            bgcolor: "#FFFFFF",
            boxShadow: "0px 2px 8px 0px #0000000D",
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                lineHeight: "20px",
                letterSpacing: 0,
                color: "#383838",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {factsFilled} of {factsTotal} facts captured
            </Typography>
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontWeight: 700,
                fontSize: 16,
                lineHeight: "20px",
                letterSpacing: 0,
                color: "#005BF0",
                flexShrink: 0,
              }}
            >
              {progressPct}%
            </Typography>
          </Box>
          <Box sx={{ height: 6, borderRadius: 999, bgcolor: "#E6E6E6", overflow: "hidden", flexShrink: 0 }}>
            <Box
              sx={{
                height: "100%",
                width: `${Math.min(100, progressPct)}%`,
                bgcolor: "#005BF0",
                borderRadius: 999,
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: "Lato, sans-serif",
              fontWeight: 400,
              fontSize: 12,
              lineHeight: "16px",
              letterSpacing: 0,
              color: "#707070",
              whiteSpace: "nowrap",
            }}
          >
            {startedShort ? `Started ${startedShort}` : "Started"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
